import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Form from "react-bootstrap/Form";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";

import type { GlobalState } from "../../types/stateTypes";
import type { WrpfCurrentRecord, WrpfRecordCategory } from "../handoff/types";
import { setRecordMarkingMode } from "../live/platformLiveProfileActions";
import type { RecordMarkingMode } from "./recordMarkingPolicy";
import { RECORD_CATEGORY_LABELS, recordCategoryLabel, recordFieldLabel, sortRecordFieldValues } from "./recordLabels";
import type { RecordField } from "./recordLabels";
import { generateRecordTestData, isRecordTestEntry, recordTestWeights, removeRecordTestData } from "./recordTestData";
import { selectEnabledRecordCategories, setWrpfRecordCategoriesOverride } from "./wrpfRecordsReducer";
import styles from "./RecordsRoute.module.scss";

interface StateProps {
  records: ReadonlyArray<WrpfCurrentRecord>;
  webRecordCategories: ReadonlyArray<WrpfRecordCategory>;
  enabledRecordCategories: ReadonlyArray<WrpfRecordCategory>;
  recordCategoriesFollowWeb: boolean;
  markingMode: RecordMarkingMode;
  recordTestToolsEnabled: boolean;
  hasGeneratedTestData: boolean;
  generatedTestScope: { day: number; platform: number } | null;
}

interface DispatchProps {
  setRecordCategories: (categories: ReadonlyArray<WrpfRecordCategory> | null) => void;
  setMarkingMode: (mode: RecordMarkingMode) => void;
  generateTestData: (record: WrpfCurrentRecord) => void;
  removeTestData: () => void;
}

type Props = StateProps & DispatchProps;

type Filters = Record<RecordField, string>;

const emptyFilters = (): Filters => ({
  jurisdiction: "",
  sex: "",
  division: "",
  weightClass: "",
  discipline: "",
  equipment: "",
  liftType: "",
});

const filterDefinitions: ReadonlyArray<{ key: RecordField; label: string; allLabel: string }> = [
  { key: "jurisdiction", label: "categoría de récord", allLabel: "Todas" },
  { key: "sex", label: "sexo", allLabel: "Todos" },
  { key: "division", label: "división", allLabel: "Todas" },
  { key: "weightClass", label: "categoría de peso", allLabel: "Todas" },
  { key: "discipline", label: "disciplina", allLabel: "Todas" },
  { key: "equipment", label: "equipamiento", allLabel: "Todos" },
  { key: "liftType", label: "levantamiento", allLabel: "Todos" },
];

const listCategories = (categories: ReadonlyArray<WrpfRecordCategory>): string => {
  const labels = categories.map(recordCategoryLabel);
  if (labels.length === 0) return "ninguna";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} y ${labels[labels.length - 1]}`;
};

const RecordsRoute = ({
  records,
  webRecordCategories,
  enabledRecordCategories,
  recordCategoriesFollowWeb,
  markingMode,
  recordTestToolsEnabled,
  hasGeneratedTestData,
  generatedTestScope,
  setRecordCategories,
  setMarkingMode,
  generateTestData,
  removeTestData,
}: Props) => {
  const [filters, setFilters] = React.useState<Filters>(emptyFilters);
  const [selectedRecord, setSelectedRecord] = React.useState<WrpfCurrentRecord | null>(null);
  const filteredRecords = records.filter((record) =>
    filterDefinitions.every(({ key }) => filters[key] === "" || record[key] === filters[key]),
  );

  // Las etiquetas de categoría de peso se repiten entre sexos ("-60kg"): solo se ofrecen las del sexo elegido.
  const optionValues = (key: RecordField): string[] => {
    const source = key === "weightClass" ? records.filter((record) => record.sex === filters.sex) : records;
    return sortRecordFieldValues(key, [...new Set(source.map((record) => String(record[key])))]);
  };

  const toggleCategory = (category: WrpfRecordCategory) =>
    setRecordCategories(
      enabledRecordCategories.includes(category)
        ? enabledRecordCategories.filter((candidate) => candidate !== category)
        : [...enabledRecordCategories, category],
    );

  return (
    <main className={styles.recordsPage} aria-labelledby="records-title">
      <div className={styles.shell}>
        <header className={styles.pageHeader}>
          <h1 id="records-title">Récords</h1>
          <p>Gestioná y marcá los intentos de récord del torneo.</p>
        </header>

        <section className={styles.modePanel} aria-labelledby="record-categories-title">
          <div className={styles.panelCopy}>
            <h2 id="record-categories-title">Récords habilitados</h2>
            {recordCategoriesFollowWeb ? (
              <p>Según la configuración del torneo en la web.</p>
            ) : (
              <p>
                Cambiado en esta PC. En la web: {listCategories(webRecordCategories)}.
                <Button
                  variant="link"
                  className={styles.resetLink}
                  aria-label="Volver a los récords habilitados en la web"
                  onClick={() => setRecordCategories(null)}
                >
                  Volver a lo de la web
                </Button>
              </p>
            )}
          </div>
          <div className={styles.categoryToggle} role="group" aria-label="Categorías de récord habilitadas">
            {RECORD_CATEGORY_LABELS.map(({ value, label }) => {
              const enabled = enabledRecordCategories.includes(value);
              return (
                <Button
                  key={value}
                  className={styles.modeButton}
                  variant={enabled ? "primary" : "outline-primary"}
                  aria-pressed={enabled}
                  onClick={() => toggleCategory(value)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </section>

        <section className={styles.modePanel} aria-labelledby="record-marking-title">
          <h2 id="record-marking-title">Marcado de intentos de récord</h2>
          <ButtonGroup className={styles.modeToggle} aria-label="Modo de marcado de récord">
            <Button
              className={styles.modeButton}
              variant={markingMode === "manual" ? "primary" : "outline-primary"}
              aria-label="Marcado manual"
              aria-pressed={markingMode === "manual"}
              onClick={() => setMarkingMode("manual")}
            >
              Manual
            </Button>
            <Button
              className={styles.modeButton}
              variant={markingMode === "automatic" ? "primary" : "outline-primary"}
              aria-label="Marcado automático"
              aria-pressed={markingMode === "automatic"}
              onClick={() => setMarkingMode("automatic")}
            >
              Automático
            </Button>
          </ButtonGroup>
        </section>

        {recordTestToolsEnabled ? (
          <section className={styles.testDataPanel} aria-labelledby="record-test-data-title">
            <h2 id="record-test-data-title">Datos de prueba</h2>
            <div className={styles.testDataContent}>
              {selectedRecord === null ? (
                <p>Seleccioná un récord de la tabla para preparar intentos reproducibles.</p>
              ) : (
                <>
                  <p>
                    Se crearán tres Assigned Entries para {recordFieldLabel("liftType", selectedRecord.liftType)}: por
                    debajo, igual y 2,5 kg por encima de la marca vigente.
                  </p>
                  <div className={styles.scenarioGrid} aria-label="Pesos de prueba">
                    {recordTestWeights(selectedRecord).map((weightKg, index) => (
                      <div key={`${index}:${weightKg}`} className={styles.scenarioCell}>
                        <small className={styles.scenarioLabel}>{["Debajo", "Igual", "Supera"][index]}</small>
                        <strong className={styles.scenarioWeight}>{weightKg} kg</strong>
                      </div>
                    ))}
                  </div>
                  <Button
                    className={styles.primaryAction}
                    aria-label={`Generar datos de prueba para ${selectedRecord.athleteName ?? "estándar vacante"}`}
                    onClick={() => generateTestData(selectedRecord)}
                  >
                    Generar datos de prueba
                  </Button>
                </>
              )}
              {hasGeneratedTestData ? (
                <Button
                  variant="outline-secondary"
                  className={selectedRecord === null ? "mt-3" : "ml-2"}
                  aria-label="Eliminar datos de prueba generados"
                  onClick={removeTestData}
                >
                  Eliminar datos de prueba
                </Button>
              ) : null}
              {generatedTestScope !== null ? (
                <Link
                  className={`btn ml-2 ${styles.operationLink}`}
                  to="/?lifting"
                  aria-label={`Abrir datos de prueba en Día ${generatedTestScope.day} Plataforma ${generatedTestScope.platform}`}
                >
                  Abrir Levantamientos · Grupo T
                </Link>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className={styles.recordsPanel} aria-labelledby="current-records-title">
          <div className={styles.filterPanel} aria-label="Filtros de récords">
            <h2 className="sr-only">Filtrar récords</h2>
            <div className={styles.filterGrid}>
              {filterDefinitions.map(({ key, label, allLabel }) => {
                const waitsForSex = key === "weightClass" && filters.sex === "";
                return (
                  <Form.Group key={key} className={styles.filterGroup}>
                    <Form.Label>{label}</Form.Label>
                    <Form.Control
                      as="select"
                      className={`custom-select ${styles.filterControl}`}
                      aria-label={`Filtrar por ${label}`}
                      value={filters[key]}
                      disabled={waitsForSex}
                      onChange={(event: React.BaseSyntheticEvent) => {
                        const value = event.currentTarget.value;
                        setFilters((previous) =>
                          key === "sex" ? { ...previous, sex: value, weightClass: "" } : { ...previous, [key]: value },
                        );
                      }}
                    >
                      <option value="">{waitsForSex ? "Elegí el sexo" : allLabel}</option>
                      {waitsForSex
                        ? null
                        : optionValues(key).map((value) => (
                            <option key={value} value={value}>
                              {recordFieldLabel(key, value)}
                            </option>
                          ))}
                    </Form.Control>
                  </Form.Group>
                );
              })}
            </div>
          </div>

          <div className={styles.tableHeader}>
            <h2 id="current-records-title">Récords vigentes</h2>
            <output aria-live="polite">
              {filteredRecords.length} de {records.length}
            </output>
          </div>
          <div className={styles.tableViewport}>
            <Table responsive hover size="sm" className={styles.recordsTable} aria-label="Récords vigentes">
              <thead>
                <tr>
                  {recordTestToolsEnabled ? <th>Prueba</th> : null}
                  <th>Categoría de récord</th>
                  <th>Sexo</th>
                  <th>División</th>
                  <th>Categoría de peso</th>
                  <th>Disciplina</th>
                  <th>Equipamiento</th>
                  <th>Levantamiento</th>
                  <th>Marca</th>
                  <th>Atleta</th>
                  <th>Torneo</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record, index) => (
                  <tr
                    key={`${record.jurisdiction}:${record.sex}:${record.division}:${record.weightClass}:${record.discipline}:${record.equipment}:${record.liftType}:${index}`}
                    className={selectedRecord === record ? styles.selectedRecordRow : undefined}
                    aria-selected={selectedRecord === record}
                  >
                    {recordTestToolsEnabled ? (
                      <td>
                        <Button
                          size="sm"
                          variant={selectedRecord === record ? "primary" : "outline-primary"}
                          aria-label={`Seleccionar récord de ${record.athleteName ?? "estándar vacante"}`}
                          aria-pressed={selectedRecord === record}
                          onClick={() => setSelectedRecord(record)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    ) : null}
                    <td>{recordFieldLabel("jurisdiction", record.jurisdiction)}</td>
                    <td>{recordFieldLabel("sex", record.sex)}</td>
                    <td>{recordFieldLabel("division", record.division)}</td>
                    <td>{recordFieldLabel("weightClass", record.weightClass)}</td>
                    <td>{recordFieldLabel("discipline", record.discipline)}</td>
                    <td>{recordFieldLabel("equipment", record.equipment)}</td>
                    <td>{recordFieldLabel("liftType", record.liftType)}</td>
                    <td>{record.weightKg} kg</td>
                    <td>{record.athleteName ?? "Estándar vacante"}</td>
                    <td>{record.tournamentName ?? "—"}</td>
                    <td>{record.recordDate ?? "—"}</td>
                  </tr>
                ))}
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={recordTestToolsEnabled ? 12 : 11} className="text-center text-muted">
                      No hay récords que coincidan con los filtros.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default connect(
  (state: GlobalState): StateProps => {
    const generatedEntry = state.registration.entries.find(isRecordTestEntry);
    return {
      records: state.wrpfRecords.currentRecords,
      webRecordCategories: state.wrpfRecords.recordCategories ?? [],
      enabledRecordCategories: selectEnabledRecordCategories(state.wrpfRecords),
      recordCategoriesFollowWeb: (state.wrpfRecords.recordCategoriesOverride ?? null) === null,
      markingMode: state.wrpfLive.recordMarking.mode,
      recordTestToolsEnabled: state.wrpfLive.developerSettings.recordTestToolsEnabled,
      hasGeneratedTestData: generatedEntry !== undefined,
      generatedTestScope:
        generatedEntry === undefined ? null : { day: generatedEntry.day, platform: generatedEntry.platform },
    };
  },
  (dispatch: ThunkDispatch<GlobalState, undefined, AnyAction>): DispatchProps => ({
    setRecordCategories: (categories) => dispatch(setWrpfRecordCategoriesOverride(categories)),
    setMarkingMode: (mode) => dispatch(setRecordMarkingMode(mode)),
    generateTestData: (record) => dispatch(generateRecordTestData(record)),
    removeTestData: () => dispatch(removeRecordTestData()),
  }),
)(RecordsRoute);
